import { Children, forwardRef, Fragment, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { DisclosureCollapse, DisclosureExpand, DisclosureNext, DisclosurePrevious } from '@sas/react-svg';
import cx from 'classnames';
import { BrowserName, getBrowserName } from '../../../browser-name';
import { usePreviousValue } from '../../../hooks/usePreviousValue';
import { useRefArray } from '../../../hooks/useRefArray';
import { useResizeObserverRef } from '../../../hooks/useResizeObserver';
import { useScroll } from '../../../hooks/useScroll';
import { useIsRTL } from '../../../locale';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { Button, ButtonVariant } from '../../Button';
import { useDragHoverOnScrollButton } from './hooks/useDragHoverOnScrollButton';
import bundle from '../NovaComponents-Scrollable-gui-icu.properties';
import './../Scrollable.css';
import cn from '../Scrollable.css.json';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
export const BaseScrollable = /*#__PURE__*/forwardRef(function BaseScrollable({
  variant = 'standard',
  ...props
}, ref) {
  let ListTag;
  const renderUsingListTags = props.renderUsingListTags ?? true;
  if (!renderUsingListTags) {
    ListTag = 'div';
  } else {
    ListTag = props.allowOrderedList ? `ol` : `ul`;
  }
  const contentRef = useRef(null);
  const contentListDivRef = useRef(null);
  const outerContainerRef = useRef(null);
  const localize = useTextLocalizer();
  const {
    refs: itemRefs,
    createSetRef: createItemRef
  } = useRefArray();
  const itemCount = Children.count(props.children);
  const [backEnabled, setBackEnabled] = useState(false);
  const [nextEnabled, setNextEnabled] = useState(true);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [focused, setFocused] = useState(false);
  const [fnResizeSensorContainer, getResizeSensorContainer] = useResizeObserverRef(() => checkForOverflow(true));
  const buttonVariant = variant === 'standard' ? ButtonVariant.Icon : ButtonVariant.PrimaryIcon;
  const isRTL = useIsRTL();
  const {
    scrollTo,
    elementIsFullyVisible,
    elementBackIsClipped,
    elementNextIsClipped
  } = useScroll(contentRef, props.orientation);
  const hideScrollButtons = props.hideScrollButtons ?? false;
  const prevCurrentIndex = usePreviousValue(props.currentIndex);
  function checkForOverflow(isResizing = false) {
    if (props.disableOverflowCheck) return;
    const currentItemRefs = props.itemRefs ? props.itemRefs : itemRefs;
    const currentItemCount = props.itemRefs ? currentItemRefs.length : itemCount;
    const containerRef = getResizeSensorContainer();
    if (!contentRef.current || !containerRef || !currentItemRefs) return;
    let nextIsOverflowing = false;
    if (props.orientation === 'vertical' && containerRef.clientHeight === 0 || props.orientation !== 'vertical' && containerRef.clientWidth === 0) {
      return;
    }
    if (currentItemCount <= 1) {
      nextIsOverflowing = false;
    } else if (props.orientation === 'vertical') {
      const btnHeight = !!isResizing && previousBtnRef.current ? 2 * previousBtnRef.current.offsetHeight : 0;
      nextIsOverflowing = contentRef.current.clientHeight + btnHeight < contentRef.current.scrollHeight;
    } else {
      const btnWidth = !!isResizing && previousBtnRef.current ? 2 * previousBtnRef.current.offsetWidth : 0;
      nextIsOverflowing = contentRef.current.clientWidth + btnWidth < contentRef.current.scrollWidth;
    }
    if (isOverflowing !== nextIsOverflowing) {
      setIsOverflowing(nextIsOverflowing);
      props.onOverflowChange?.(nextIsOverflowing);
    }

    // the empty <ul> element's scrollWidth may be 1px but its parent element's clientWidth is 0px in Safari,
    // which makes the navigation buttons shown in Safari.
    // So adding a additional condition to avoid wrong overflow status.

    let currentItemIndex = -1;
    // if focus is inside the list scroll to currentIndex. Else, if focused moved out of the
    // list maintain the focus at the selectedIndex.
    if (focused || !focused && props.selectedIndex === undefined || props.suppressScrollOnBlur) currentItemIndex = props.currentIndex;else currentItemIndex = props.selectedIndex !== undefined ? props.selectedIndex : -1;
    if (nextIsOverflowing && currentItemIndex !== -1 && currentItemRefs[currentItemIndex]) {
      let direction = null;
      // deleted items ref's are stored as null in currentItemRefs
      const currentRefs = currentItemRefs.filter(e => e !== null);
      if (currentItemIndex === currentRefs.length - 1 || prevCurrentIndex && prevCurrentIndex - currentItemIndex === -1) direction = props.orientation !== 'vertical' && isRTL ? 'back' : 'next';
      if (currentItemIndex === 0 || prevCurrentIndex && prevCurrentIndex - currentItemIndex === 1) direction = props.orientation !== 'vertical' && isRTL ? 'next' : 'back';
      if (!props.hideScrollButtons) {
        scrollTo(currentItemRefs[currentItemIndex], direction);
        if (currentItemRefs[currentItemCount - 1] && elementNextIsClipped(currentItemRefs[currentItemCount - 1]) !== nextEnabled) {
          setNextEnabled(elementNextIsClipped(currentItemRefs[currentItemCount - 1]));
        } else if (currentItemRefs[0] && elementBackIsClipped(currentItemRefs[0]) !== backEnabled) {
          setBackEnabled(elementBackIsClipped(currentItemRefs[0]));
        }
      } else {
        setTimeout(() => scrollTo(currentItemRefs[currentItemIndex], direction), 0);
      }
    }
  }
  useLayoutEffect(checkForOverflow);
  const setOuterContainerRef = useCallback(el => {
    outerContainerRef.current = el;
    setRef(ref, el);
  }, [ref]);
  const changeSelection = useCallback(direction => {
    const currentItemRefs = props.itemRefs ? props.itemRefs : itemRefs,
      currentItemCount = props.itemRefs ? currentItemRefs.length : itemCount;
    if (!contentRef.current || !currentItemCount) return;
    const itemVisibilityMap = currentItemRefs.map(itemRef => itemRef && elementIsFullyVisible(itemRef));
    if (direction === 'back') {
      const firstVisible = itemVisibilityMap.indexOf(true);
      let prevPossibleIndex = props.currentIndex - 1,
        prevPossibleLastIndex = firstVisible - 1;
      for (let i = prevPossibleLastIndex; i >= 0; i--) {
        if (itemVisibilityMap[i] !== undefined) {
          prevPossibleLastIndex = i;
          break;
        }
      }
      for (let i = prevPossibleIndex; i >= 0; i--) {
        if (itemVisibilityMap[i] !== undefined) {
          prevPossibleIndex = i;
          break;
        }
      }
      props.setCurrentIndex(Math.max(0, firstVisible === -1 ? prevPossibleIndex : prevPossibleLastIndex));
    } else {
      const lastVisible = itemVisibilityMap.lastIndexOf(true);
      const nextPossibleLastIndex = itemVisibilityMap.findIndex((_, i) => {
        return i > lastVisible && itemVisibilityMap[i] !== undefined;
      });
      let nextPossibleIndex = itemVisibilityMap.findIndex((_, i) => {
        return i > props.currentIndex && itemVisibilityMap[i] !== undefined;
      });
      nextPossibleIndex = nextPossibleIndex === -1 ? props.currentIndex : nextPossibleIndex;
      props.setCurrentIndex(Math.min(currentItemCount - 1, lastVisible === -1 ? nextPossibleIndex : nextPossibleLastIndex));
    }
  }, [elementIsFullyVisible, itemCount, itemRefs, props]);
  const contentClassNames = cx(cn.scrollContent, props.contentClassName, props.contentProps?.className, hideScrollButtons && cn[`showScrollBar${props.orientation === 'vertical' ? 'Y' : 'X'}`]);
  const Tag = !renderUsingListTags ? 'div' : 'li';
  const items = Children.map(props.children, (value, i) => /*#__PURE__*/_jsxs(Fragment, {
    children: [i > 0 ? props.separator : null, /*#__PURE__*/_jsx(Tag, {
      ref: createItemRef(i),
      className: props.itemClassName?.(i),
      role: props.contentProps && props.contentProps.role ? 'presentation' : undefined,
      onFocus: () => {
        setFocused(true);
      },
      children: value
    })]
  }, i));
  const attributesOfList = {
    ref: scrollableRef => {
      contentRef.current = scrollableRef;
      setRef(props.contentRef, scrollableRef);
    },
    ...{
      ...props.contentProps
    },
    className: contentClassNames,
    onScroll: props.onScroll,
    onScrollCapture: () => {
      if (hideScrollButtons) return;
      const currentItemRefs = props.itemRefs ? props.itemRefs : itemRefs,
        currentItemCount = props.itemRefs ? currentItemRefs.length : itemCount;
      if (!contentRef.current || !currentItemCount) return;
      const firstItem = currentItemRefs[0];
      const lastItem = currentItemRefs[currentItemCount - 1];
      firstItem && setBackEnabled(elementBackIsClipped(firstItem));
      lastItem && setNextEnabled(elementNextIsClipped(lastItem));
    },
    style: {
      ...props.contentProps?.style,
      ...(getBrowserName() === BrowserName.Safari && {
        scrollBehavior: 'auto'
      })
    }
  };
  const previousBtnRef = useRef(null);
  const nextBtnRef = useRef(null);
  useDragHoverOnScrollButton(previousBtnRef, props.acceptDropTypes ?? [], useCallback(() => {
    if (backEnabled) {
      setFocused(true);
      changeSelection('back');
    }
  }, [backEnabled, changeSelection]));
  useDragHoverOnScrollButton(nextBtnRef, props.acceptDropTypes ?? [], useCallback(() => {
    if (nextEnabled) {
      setFocused(true);
      changeSelection('next');
    }
  }, [nextEnabled, changeSelection]));
  return /*#__PURE__*/_jsx("div", {
    className: cx(props.className, props.orientation === 'vertical' ? cn.vertical : cn.horizontal),
    style: {
      ...props.style
    },
    ref: setOuterContainerRef,
    "data-testid": props.testId,
    onFocus: props.onFocus,
    role: props.role,
    "aria-label": props.ariaLabel,
    "data-landmark-label": props.landmarkLabel,
    children: /*#__PURE__*/_jsxs("div", {
      className: cn.scrollContainer,
      ref: fnResizeSensorContainer,
      onBlur: e => {
        if (props.selectedIndex !== undefined && !getResizeSensorContainer()?.contains(e.nativeEvent.relatedTarget)) {
          const currentItemRefs = props.itemRefs ? props.itemRefs : itemRefs;
          const currentItem = currentItemRefs[props.selectedIndex] ?? null;
          if (!props.suppressScrollOnBlur && currentItem) {
            scrollTo(currentItem);
            props.setCurrentIndex(props.selectedIndex);
          }
          setFocused(false);
        }
      },
      children: [!hideScrollButtons && isOverflowing && /*#__PURE__*/_jsx(Button, {
        ref: previousBtnRef,
        testId: postfixTestId(props.testId, 'previousBtn'),
        className: cn.scrollButton,
        variant: buttonVariant,
        disabled: !backEnabled,
        focusable: false,
        onPress: () => {
          setFocused(true);
          changeSelection('back');
        },
        ariaLabel: localize('back.button.aria'),
        icon: {
          icon: props.orientation === 'vertical' ? DisclosureCollapse : DisclosurePrevious,
          tooltip: '',
          ariaLabel: ''
        },
        enableContinuousPress: true
      }), /*#__PURE__*/_jsx("div", {
        className: cx(props.scrollWindowClassName, cn.fadeContainer),
        ref: element => {
          contentListDivRef.current = element;
          setRef(props.scrollWindowRef, element);
        },
        "data-testid": postfixTestId(props.testId, 'scrollWindow'),
        children: /*#__PURE__*/_jsx(ListTag, {
          ...attributesOfList,
          "aria-describedby": props.contentAriaDescribedBy,
          children: items
        })
      }), !hideScrollButtons && isOverflowing && /*#__PURE__*/_jsx(Button, {
        ref: nextBtnRef,
        testId: postfixTestId(props.testId, 'nextBtn'),
        className: cn.scrollButton,
        variant: buttonVariant,
        disabled: !nextEnabled,
        focusable: false,
        onPress: () => {
          setFocused(true);
          changeSelection('next');
        },
        ariaLabel: localize('next.button.aria'),
        icon: {
          icon: props.orientation === 'vertical' ? DisclosureExpand : DisclosureNext,
          ariaLabel: '',
          tooltip: ''
        },
        enableContinuousPress: true
      })]
    })
  });
});
//# sourceMappingURL=BaseScrollable.js.map